import styled, { keyframes } from "styled-components";

import { fontSizes, lineHeights } from "../../../shared/styles/typography";
import { pageRoot } from "../../../styles/pageRoot";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

export const Container = styled.div`
  ${pageRoot}
  background-color: var(--color-background);
`;

export const HeaderContainer = styled.div`
  padding: 1rem 2rem;
  background-color: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: 1rem;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border-radius: 50%;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--color-border);
    }
  }

  h2 {
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
    font-weight: var(--font-weight-semibold);
    margin: 0;
  }
`;

export const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--color-border);
    border-radius: 3px;
  }
`;

export const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 70%;
  padding: 1rem 1.25rem;
  border-radius: 1.25rem;
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  animation: ${fadeIn} 0.3s ease-out;
  position: relative;
  align-self: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  background-color: ${(props) =>
    props.isUser ? "var(--color-primary)" : "var(--color-surface)"};
  color: ${(props) => (props.isUser ? "white" : "var(--color-text)")};
  border: ${(props) =>
    props.isUser ? "none" : "1px solid var(--color-border)"};
  border-bottom-right-radius: ${(props) =>
    props.isUser ? "0.25rem" : "1.25rem"};
  border-bottom-left-radius: ${(props) =>
    props.isUser ? "1.25rem" : "0.25rem"};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .timestamp {
    font-size: ${fontSizes.xs}px;
    line-height: ${lineHeights.xs}px;
    margin-top: 0.5rem;
    opacity: 0.7;
    text-align: right;
  }
`;

export const BotAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
`;

export const DactylologyContainer = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);

  p {
    font-size: ${fontSizes.sm}px;
    line-height: ${lineHeights.sm}px;
    color: var(--color-text-secondary);
    margin-bottom: 0.75rem;
  }

  .letters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;

    span {
      font-family: "Libras2020", sans-serif;
      font-size: ${fontSizes["3xl"]}px;
      color: var(--color-primary);
    }
  }

  button {
    background-color: rgba(var(--color-primary-rgb), 0.1);
    color: var(--color-primary);
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: ${fontSizes.sm}px;
    line-height: ${lineHeights.sm}px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: rgba(var(--color-primary-rgb), 0.2);
    }
  }
`;

export const InputArea = styled.div`
  padding: 1.5rem 2rem;
  background-color: var(--color-surface);
  border-top: 1px solid var(--color-border);
  display: flex;
  align-items: flex-end;
  gap: 1rem;

  textarea {
    flex: 1;
    background-color: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: 1.5rem;
    padding: 0.75rem 1.25rem;
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
    color: var(--color-text);
    resize: none;
    max-height: 150px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: var(--color-primary);
    }

    &::placeholder {
      color: var(--color-text-secondary);
    }
  }

  .send-button {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: var(--color-primary);
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;

    &:disabled {
      background-color: var(--color-border);
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      transform: scale(1.05);
      background-color: var(--color-primary-dark);
    }
  }
`;

export const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 8px;
    height: 8px;
    background-color: var(--color-text-secondary);
    border-radius: 50%;
    display: inline-block;
    animation: ${bounce} 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
`;
